import React, { useState, useEffect, useCallback } from 'react';
import { adminFileAPI } from '../services/api';
import FileViewModal from '../components/FileViewModal';
import ConfirmationModal from '../components/ConfirmationModal';
import RepliesModal from '../components/RepliesModal';

const AdminFileManagement = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 5,
    title: '',
    description: '',
    sortBy: 'created_date',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRepliesOpen, setIsRepliesOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminFileAPI.getAllSubmittedFiles(filters);
      if (response.data.success) {
        setFiles(response.data.data.files);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      setError('Failed to fetch files');
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'reject': return '#dc3545';
      case 'pending': return '#ffc107';
      case 'process': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'reject': return 'Rejected';
      case 'pending': return 'Pending';
      case 'process': return 'Processing';
      default: return 'Unknown';
    }
  };

  const handleStatusUpdate = async (fileId, newStatus) => {
    setUpdatingStatus(fileId);
    try {
      const response = await adminFileAPI.updateFileStatus({
        _id:fileId,
        status: newStatus,
      });

      if (response.data.success) {
        setSuccess('File status updated successfully');
        fetchFiles(); // Refresh the list
        setTimeout(() => setSuccess(''), 3000);
        return { success: true };
      } else {
        setError(response.data.message || 'Failed to update status');
        setTimeout(() => setError(''), 3000);
        return { success: false };
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update file status');
      setTimeout(() => setError(''), 3000);
      return { success: false };
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const handleOpenReplies = (file) => {
    setSelectedFile(file);
    setIsRepliesOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
    });
  };

  const validStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'process', label: 'Processing' },
    { value: 'approved', label: 'Approved' },
    { value: 'reject', label: 'Rejected' },
  ];

  if (loading && files.length === 0) {
    return (
      <div className="d-flex justify-center align-center" style={{ minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ marginBottom: '0.5rem' }}>File Management</h1>
        <p style={{ color: 'var(--gray-600)' }}>
          Review and manage all user-submitted files.
        </p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-body">
          <div className="grid grid-cols-4" style={{ gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Search Title</label>
              <input
                type="text"
                name="title"
                className="form-input"
                value={filters.title}
                onChange={handleFilterChange}
                placeholder="Search by title"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Search Description</label>
              <input
                type="text"
                name="description"
                className="form-input"
                value={filters.description}
                onChange={handleFilterChange}
                placeholder="Search by description"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sort By</label>
              <select
                name="sortBy"
                className="form-input"
                value={filters.sortBy}
                onChange={handleFilterChange}
              >
                <option value="created_date">Date Created</option>
                <option value="title">Title</option>
                <option value="user_id">User</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <select
                name="sortOrder"
                className="form-input"
                value={filters.sortOrder}
                onChange={handleFilterChange}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Files List */}
      {files.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{
                fontSize: '4rem',
                marginBottom: '1rem',
                opacity: 0.5,
              }}>
                📂
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-600)' }}>
                No files found
              </h3>
              <p style={{ margin: 0, color: 'var(--gray-500)' }}>
                Try adjusting your search criteria.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="file-list">
              {files.map((file) => (
                <div key={file._id} className="file-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                      {file.title || file.original_filename}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'var(--gray-600)',
                      marginBottom: '0.25rem',
                    }}>
                      {file.description || 'No description'}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--gray-500)',
                      marginBottom: '0.25rem',
                    }}>
                      User: {file.user_id?.name || file.user_id?.email} • {formatDate(file.created_date)}
                    </div>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.125rem 0.25rem',
                      borderRadius: 'var(--border-radius-sm)',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(file.status),
                      color: 'var(--white)',
                    }}>
                      {getStatusText(file.status)}
                    </div>
                  </div>
                  <div className="d-flex align-center" style={{ gap: '0.5rem' }}>
                    <button
                      onClick={() => handleViewFile(file)}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleOpenReplies(file)}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      Replies {Array.isArray(file.admin_responses) && file.admin_responses.length ? `(${file.admin_responses.length})` : ''}
                    </button>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={file.status || 'pending'}
                                              onChange={(e) => {
                        const newStatus = e.target.value;
                        const currentStatusText = getStatusText(file.status);
                        const newStatusText = getStatusText(newStatus);

                        setConfirmationModal({
                          isOpen: true,
                          title: 'Confirm Status Change',
                          message: `Are you sure you want to change the status from "${currentStatusText}" to "${newStatusText}"?\n\nThis action cannot be undone.`,
                          onConfirm: () => {
                            handleStatusUpdate(file._id, newStatus);
                          },
                        });

                        // Reset the select to current value initially
                        e.target.value = file.status || 'pending';
                      }}
                        disabled={updatingStatus === file._id || (file.status !== 'pending' && file.status !== 'process')}
                        style={{
                          padding: '0.25rem',
                          borderRadius: 'var(--border-radius-sm)',
                          border: '1px solid var(--border-color)',
                          fontSize: '0.75rem',
                          opacity: (file.status === 'pending' || file.status === 'process') ? 1 : 0.6,
                        }}
                      >
                        {validStatuses.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                      {file.status !== 'pending' && file.status !== 'process' && (
                        <span
                          title="Status can only be updated when current status is Pending or Processing"
                          style={{
                            position: 'absolute',
                            right: '-15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.75rem',
                            color: '#ffc107',
                            cursor: 'help',
                          }}
                        >
                          ℹ️
                        </span>
                      )}
                    </div>
                    {updatingStatus === file._id && (
                      <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="card-footer">
              <div className="d-flex justify-between align-center">
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Showing {((pagination.currentPage - 1) * pagination.filesPerPage) + 1} to{' '}
                  {Math.min(pagination.currentPage * pagination.filesPerPage, pagination.totalFiles)} of{' '}
                  {pagination.totalFiles} files
                </div>
                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    Previous
                  </button>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}>
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* File View Modal */}
      <FileViewModal
        file={selectedFile}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStatusUpdate={handleStatusUpdate}
      />

      {/* Replies Modal */}
      <RepliesModal
        isOpen={isRepliesOpen}
        onClose={() => setIsRepliesOpen(false)}
        replies={selectedFile?.admin_responses || []}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
      />
    </div>
  );
};

export default AdminFileManagement;
